import React, { memo, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  ContactInteractionUnion,
  ContactInteractionDirection,
  ContactInteractionPhoneCall,
  ContactInteractionParticipatingEntityEdge,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { matchContactInteractionExhaustive } from '@lgg/isomorphic/utils/match-contact-interaction';
import {
  ContactInteractionDetailsContextMenu,
  ContactInteractionDetailsMenuTitle,
} from 'src/components/domain/contact-interaction/contact-interaction-detail-item';
import { InProgressCallIndicator } from 'src/components/domain/contact-interaction/in-progress-call-indicator';
import { ManualRegistrationIcon } from 'src/components/domain/contact-interaction/manual-registration-icon';
import { LggPopover } from 'src/components/general/display/popover';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useUrls } from 'src/hooks/use-urls';
import { getActivityIcon } from '../helpers';
import { ActivityIconWithDirection } from './activity-icon-with-direction';

const ActivityTitleContainer = styled(FlexRow)`
  position: relative;
  width: 100%;

  ${up('md')} {
    align-items: center;
  }
`;

const ActivityDescription = styled.span`
  color: ${({ theme }) => theme.colors.storm};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  white-space: break-spaces;
  text-indent: 19px;

  & > span {
    line-height: 14px;
  }

  ${up('md')} {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-indent: unset;
    line-height: 17px;

    & > * {
      line-height: 15px;
    }
  }
`;

const MobileActivityIcon = styled(Icon)`
  position: absolute;
  top: 1px;

  svg {
    width: 14px;
    height: 14px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const StrongText = styled.span<{ danger?: boolean }>`
  font-family: ${({ theme }) => theme.font.medium};
  color: ${({ theme }) => theme.colors.storm};

  ${({ danger, theme }) =>
    danger &&
    `
    color: ${theme.colors.secondaryCoral80};
  `}
`;

const ActivityDescriptionWrapper = styled.span`
  cursor: pointer;
  line-height: 18px;

  &,
  &:visited {
    color: ${({ theme }) => theme.colors.storm};
  }

  ${up('md')} {
    line-height: 15px;

    &:hover {
      &,
      ${StrongText} {
        color: ${({ theme }) => theme.colors.gogo};
      }
    }
  }
`;

const InteractionBadgeContainer = styled(FlexRow)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 9px;
  border: solid 1px rgba(152, 169, 188, 0.25);
  height: 18px;
  margin-right: 10px;
  max-width: 30%;
  padding: 2px 5px;
`;

const InteractionBadgeIcon = styled(Icon)`
  margin-right: 5px;
  svg {
    width: 12px;
    height: 12px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const InteractionBadgeText = styled.p`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 11px;
  line-height: 13px;
  letter-spacing: -0.22px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-wrap: nowrap;
  white-space: nowrap;
`;

const ActivityBadgesContainer = styled(FlexRow)`
  margin-top: 8px;

  ${up('md')} {
    margin-top: 4px;
  }
`;

const TitleContentContainer = styled(FlexColumn)`
  width: 100%;

  ${up('md')} {
    width: calc(100% - 36px);
  }
`;

type InteractionInfoBadgeProps = {
  icon: string;
  text: string;
  ['data-lgg-id']: string;
};

const InteractionInfoBadge = ({ icon, text, ...rest }: InteractionInfoBadgeProps) => {
  return (
    <InteractionBadgeContainer {...rest}>
      <InteractionBadgeIcon type={icon} />
      <InteractionBadgeText>{text}</InteractionBadgeText>
    </InteractionBadgeContainer>
  );
};

type ContactInteractionDetailsPopoverProps = {
  title: string;
  details: JSX.Element;
  children?: JSX.Element;
};

const ContactInteractionDetailsPopover = ({
  children,
  details,
  title,
}: ContactInteractionDetailsPopoverProps) => {
  const breakpointUpMd = useBreakpoint(up('md'));
  const popoverTrigger = <span data-lgg-id="activity-popover-trigger">{children}</span>;

  return breakpointUpMd ? (
    <LggPopover
      trigger={['hover']}
      placement="topLeft"
      align={{
        offset: [0, 3],
      }}
      overlayClassName="activity-details-popover"
      content={
        <ContactInteractionDetailsContextMenu data-lgg-id="activity-details-popover">
          <ContactInteractionDetailsMenuTitle>{title}</ContactInteractionDetailsMenuTitle>
          {details}
        </ContactInteractionDetailsContextMenu>
      }
    >
      {popoverTrigger}
    </LggPopover>
  ) : (
    popoverTrigger
  );
};

type ActivityTitleProps = {
  contactInteraction: ContactInteractionUnion;
  contactInteractionDetails: JSX.Element;
  direction?: ContactInteractionDirection;
};

export const ActivityTitle = memo<ActivityTitleProps>(
  ({ contactInteraction, contactInteractionDetails, direction }) => {
    const isUnansweredCall =
      contactInteraction.__typename === 'ContactInteractionPhoneCall' &&
      contactInteraction.dialStatus === 'UNANSWERED';
    const { t } = useTranslation(['activity', 'common']);
    const breakpointUpMd = useBreakpoint(up('md'));
    const activityIconName = getActivityIcon({
      type: contactInteraction.__typename,
      isUnansweredCall: isUnansweredCall,
    });
    const { getContactInteractionUrl } = useUrls();
    const contactInteractionUrl = getContactInteractionUrl({
      contactInteractionId: contactInteraction.id,
    });

    const activityTextDescription = useMemo(() => {
      const components = { strong: <StrongText />, danger: <StrongText danger /> };
      const callInProgressLabel = <InProgressCallIndicator />;
      const getInboundAnsweredByTitle = ({
        participatingEntity,
      }:
        | ContactInteractionPhoneCall
        | (ContactInteractionPhoneCall & {
            participatingEntity: ContactInteractionParticipatingEntityEdge;
          })) => {
        if (participatingEntity) {
          return (
            <Trans
              i18nKey="activity:interaction.call.inboundAnsweredWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity.label,
              }}
              components={components}
            />
          );
        }

        return (
          <Trans
            i18nKey="activity:interaction.call.inboundAnsweredWithoutParticipatingEntity"
            components={components}
          />
        );
      };

      return matchContactInteractionExhaustive(contactInteraction, {
        phone: {
          defaultInbound: () => (
            <Trans
              i18nKey="activity:interaction.call.defaultInbound"
              components={components}
            />
          ),
          defaultOutbound: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.defaultOutbound"
              values={{ userName: participatingEntity?.label }}
              components={components}
            />
          ),
          inboundManualRegistrationWithParticipatingEntity: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.inboundManualRegistrationWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity.label,
              }}
              components={components}
            />
          ),
          inboundManualRegistrationWithoutParticipatingEntity: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundManualRegistrationWithoutParticipatingEntity"
              components={components}
            />
          ),
          inboundAnsweredWithParticipatingEntity: getInboundAnsweredByTitle,
          inboundAnsweredWithoutParticipatingEntity: getInboundAnsweredByTitle,
          inboundUnansweredWithDialStepMenu: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundUnansweredWithDialStepMenu"
              components={components}
            />
          ),
          inboundUnansweredWithDialStepMessage: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundUnansweredWithDialStepMessage"
              components={components}
            />
          ),
          inboundUnansweredWithDialStepDial: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundUnansweredWithDialStepDial"
              components={components}
            />
          ),
          inboundUnansweredWithDialStepVoicemailWithRecording: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundUnansweredWithDialStepVoicemailWithRecording"
              components={components}
            />
          ),
          inboundUnansweredWithDialStepVoicemailWithoutRecording: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundUnansweredWithDialStepVoicemailWithoutRecording"
              components={components}
            />
          ),
          inboundUnansweredWithoutDialStep: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundUnansweredWithoutDialStep"
              components={components}
            />
          ),
          inboundOtherStatusDialStepMenu: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundOtherStatusDialStepMenu"
              components={components}
            />
          ),
          inboundOtherStatusDialStepMessage: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundOtherStatusDialStepMessage"
              components={components}
            />
          ),
          inboundOtherStatusDialStepDial: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundOtherStatusDialStepDial"
              components={components}
            />
          ),
          inboundOtherStatusDialStepVoicemailWithRecording: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundOtherStatusDialStepVoicemailWithRecording"
              components={components}
            />
          ),
          inboundOtherStatusDialStepVoicemailWithoutRecording: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundOtherStatusDialStepVoicemailWithoutRecording"
              components={components}
            />
          ),
          inboundOtherStatusNoStep: () => (
            <Trans
              i18nKey="activity:interaction.call.inboundOtherStatusNoStep"
              components={components}
            />
          ),
          outboundManualRegistration: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundManualRegistration"
              components={components}
              values={{ originator: participatingEntity?.label }}
            />
          ),
          outboundAnswered: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundAnswered"
              values={{ originator: participatingEntity?.label }}
              components={components}
            />
          ),
          outboundWithCancelReason: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundWithCancelReason"
              components={components}
              values={{ originator: participatingEntity?.label }}
            />
          ),
          outboundUnansweredWithBusyReason: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundUnansweredWithBusyReason"
              components={components}
              values={{ originator: participatingEntity?.label }}
            />
          ),
          outboundUnansweredWithFailedReason: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundUnansweredWithFailedReason"
              components={components}
              values={{ originator: participatingEntity?.label }}
            />
          ),
          outboundUnansweredWithOtherReason: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundUnansweredWithOtherReason"
              components={components}
              values={{ originator: participatingEntity?.label }}
            />
          ),
          outboundOtherStatus: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.call.outboundOtherStatus"
              components={components}
              values={{ originator: participatingEntity?.label }}
            />
          ),
          inboundAnsweredWithParticipatingEntityInProgress: (interaction) => (
            <>
              {getInboundAnsweredByTitle(interaction)}
              {callInProgressLabel}
            </>
          ),
          inboundAnsweredWithoutParticipatingEntityInProgress: (interaction) => (
            <>
              {getInboundAnsweredByTitle(interaction)}
              {callInProgressLabel}
            </>
          ),
          inboundInProgress: () => (
            <>
              <Trans
                i18nKey="activity:interaction.call.defaultInbound"
                components={components}
              />
              {callInProgressLabel}
            </>
          ),
          outboundInProgress: ({ participatingEntity }) => (
            <>
              <Trans
                i18nKey="activity:interaction.call.defaultOutbound"
                values={{ originator: participatingEntity?.label }}
                components={components}
              />
              {callInProgressLabel}
            </>
          ),
        },
        sms: {
          inboundWithParticipatingEntity: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.sms.inboundWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity?.label,
              }}
              components={components}
            />
          ),
          inboundWithoutParticipatingEntity: () => (
            <Trans
              i18nKey="activity:interaction.sms.inboundWithoutParticipatingEntity"
              components={components}
            />
          ),
          outbound: ({ participatingEntity }) => {
            const originatorLabel = participatingEntity?.label;

            const translationKey = match(participatingEntity)
              .with(
                { type: 'CALLFLOW' },
                () => 'activity:interaction.sms.outbound.callflow' as const,
              )
              .with(
                { type: 'BROADCAST' },
                () => 'activity:interaction.sms.outbound.broadcast' as const,
              )
              .otherwise(() => 'activity:interaction.sms.outbound.default' as const);

            return (
              <Trans
                i18nKey={translationKey}
                values={{
                  originator: originatorLabel,
                }}
                components={components}
              />
            );
          },
        },
        facebookMessenger: {
          inboundWithParticipatingEntity: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.facebookMessenger.inboundWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity.label,
              }}
              components={components}
            />
          ),
          inboundWithoutParticipatingEntity: () => (
            <Trans
              i18nKey="activity:interaction.facebookMessenger.inboundWithoutParticipatingEntity"
              components={components}
            />
          ),
          outbound: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.facebookMessenger.outbound"
              values={{
                originator: participatingEntity?.label,
              }}
              components={components}
            />
          ),
        },
        instagram: {
          inboundWithParticipatingEntity: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.instagram.inboundWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity.label,
              }}
              components={components}
            />
          ),
          inboundWithoutParticipatingEntity: () => (
            <Trans
              i18nKey="activity:interaction.instagram.inboundWithoutParticipatingEntity"
              components={components}
            />
          ),
          outbound: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.instagram.outbound"
              values={{
                originator: participatingEntity?.label,
              }}
              components={components}
            />
          ),
        },
        email: {
          inboundWithParticipatingEntity: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.email.inboundWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity.label,
              }}
              components={components}
            />
          ),
          inboundWithoutParticipatingEntity: () => (
            <Trans
              i18nKey="activity:interaction.email.inboundWithoutParticipatingEntity"
              components={components}
            />
          ),
          outbound: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.email.outbound"
              values={{
                originator: participatingEntity?.label,
              }}
              components={components}
            />
          ),
        },
        inPerson: ({ participatingEntity }) => (
          <Trans
            i18nKey="activity:interaction.inPerson"
            values={{ participatingEntityLabel: participatingEntity?.label }}
            components={components}
          />
        ),
        facebookAd: () => (
          <Trans i18nKey="activity:interaction.facebookAd" components={components} />
        ),
        whatsapp: {
          inboundWithParticipatingEntity: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.whatsapp.inboundWithParticipatingEntity"
              values={{
                participatingEntityLabel: participatingEntity.label,
              }}
              components={components}
            />
          ),
          inboundWithoutParticipatingEntity: () => (
            <Trans
              i18nKey="activity:interaction.whatsapp.inboundWithoutParticipatingEntity"
              components={components}
            />
          ),
          outbound: ({ participatingEntity }) => (
            <Trans
              i18nKey="activity:interaction.whatsapp.outbound"
              values={{
                originator: participatingEntity?.label,
              }}
              components={components}
            />
          ),
        },
        unknown: () => (
          <Trans i18nKey="activity:interaction.unknown" components={components} />
        ),
      });
    }, [contactInteraction]);

    const { source, department, campaign } = contactInteraction;

    const showBadgeContainer = source?.name || department?.name || campaign?.name;

    return (
      <ActivityTitleContainer data-lgg-id="activity-table-cell-title">
        {breakpointUpMd ? (
          <ActivityIconWithDirection icon={activityIconName} direction={direction} />
        ) : (
          <MobileActivityIcon type={activityIconName} lggTestId="activity-icon" />
        )}
        <TitleContentContainer>
          <ActivityDescription data-lgg-id="activity-item-description">
            <ContactInteractionDetailsPopover
              title={t('activity:activityDetailsPopover.title')}
              details={contactInteractionDetails}
            >
              {breakpointUpMd ? (
                <ActivityDescriptionWrapper
                  data-lgg-id="activity-contact-interaction-link"
                  as={Link}
                  to={contactInteractionUrl}
                >
                  {activityTextDescription}
                </ActivityDescriptionWrapper>
              ) : (
                <ActivityDescriptionWrapper>
                  {activityTextDescription}
                </ActivityDescriptionWrapper>
              )}
            </ContactInteractionDetailsPopover>
            <ManualRegistrationIcon contactInteraction={contactInteraction} />
          </ActivityDescription>
          {showBadgeContainer && (
            <ActivityBadgesContainer>
              {source?.name && (
                <InteractionInfoBadge
                  icon="activitySource"
                  text={source.name}
                  data-lgg-id="activity-source-badge"
                />
              )}
              {campaign?.name && (
                <InteractionInfoBadge
                  icon="activityCampaign"
                  text={campaign.name}
                  data-lgg-id="activity-campaign-badge"
                />
              )}
              {department?.name && (
                <InteractionInfoBadge
                  icon="activityDepartment"
                  text={department.name}
                  data-lgg-id="activity-department-badge"
                />
              )}
            </ActivityBadgesContainer>
          )}
        </TitleContentContainer>
      </ActivityTitleContainer>
    );
  },
);
